import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Empty, Badge } from "@/components/ui-kit";
import { Plus, Trash2, Tags, CalendarRange, Users, User } from "lucide-react";
import { toast } from "sonner";

const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };

export default function Categories() {
  const { can } = useAuth();
  const isAdmin = can("categories_manage");
  const [cats, setCats] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [resetting, setResetting] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    await api.post("/categories", { ...form, fee_amount: 150 });
    toast.success("Catégorie créée"); setOpen(false); setForm({ name: "", description: "" }); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/categories/${id}`); load(); };

  const genBirthYears = async () => {
    if (!window.confirm("Remplacer TOUTES les catégories par les années de naissance (2016 → 2024) ? Les affiliés seront reclassés selon leur date de naissance.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/categories/reset-birth-years", { fee_amount: 150 });
      toast.success(`${data.created} catégories créées · ${data.remapped} affilié(s) reclassé(s)`);
      load();
    } catch { toast.error("Erreur lors de la génération"); }
    finally { setResetting(false); }
  };

  const isPlayer = (m) => { const f = m.functions; return !f || f.length === 0 || f.includes("joueur"); };
  const playersOf = (name) => members
    .filter((m) => m.category === name && isPlayer(m))
    .sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "", "fr"));

  const sorted = [...cats].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));
  const selPlayers = selected ? playersOf(selected.name) : [];

  return (
    <div>
      <PageHeader title="Catégories" subtitle="Catégories par année de naissance. Cliquez une année pour voir ses joueurs."
        action={isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="gen-birth-years-btn" variant="outline" onClick={genBirthYears} disabled={resetting}>
              <CalendarRange size={16} /> {resetting ? "Génération..." : "Années de naissance"}
            </Button>
            <Button data-testid="add-category-btn" onClick={() => setOpen(true)}><Plus size={16} /> Nouvelle</Button>
          </div>
        )} />

      {sorted.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((c) => {
            const count = playersOf(c.name).length;
            return (
              <Card key={c.id} data-testid="category-card"
                onClick={() => setSelected(c)}
                className="cursor-pointer hover:-translate-y-1 hover:border-primary/50 transition-all duration-200">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center text-primary"><Tags size={20} /></div>
                  {isAdmin && <button data-testid="delete-category-btn" onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>}
                </div>
                <p className="font-display text-2xl font-black uppercase mt-3">{c.name}</p>
                <p className="text-sm text-muted-foreground mt-1">{c.description || "—"}</p>
                <div className="mt-3 flex items-center gap-2 text-primary font-bold" data-testid={`category-count-${c.name}`}>
                  <Users size={16} /> {count} joueur{count > 1 ? "s" : ""}
                </div>
              </Card>
            );
          })}
        </div>
      ) : <Empty text="Générez les années de naissance (2016 → 2024) ou créez une catégorie." />}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Joueurs · ${selected?.name || ""}`}>
        <div className="flex items-center gap-2 mb-4" data-testid="selected-category-count">
          <Badge><Users size={13} className="mr-1" /> {selPlayers.length} joueur{selPlayers.length > 1 ? "s" : ""}</Badge>
        </div>
        {selPlayers.length ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {selPlayers.map((m) => (
              <div key={m.id} data-testid="category-player-row" className="flex items-center gap-3 p-2.5 rounded-lg bg-secondary/40">
                <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary shrink-0"><User size={16} /></div>
                <span className="font-semibold text-sm" style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>{m.first_name} {m.last_name}</span>
                {m.jersey_number ? <span className="ml-auto text-xs text-muted-foreground">#{m.jersey_number}</span> : null}
              </div>
            ))}
          </div>
        ) : <Empty text="Aucun joueur dans cette catégorie." />}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle catégorie">
        <form onSubmit={save}>
          <Field label="Nom (année de naissance)"><Input data-testid="category-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2016" /></Field>
          <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Né(e) en 2016" /></Field>
          <Button data-testid="save-category-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>
    </div>
  );
}
